import * as React from 'react';

import * as Schema from 'nutshell-core/schema';
import type {FilterObject, EntityListType} from 'nutshell-core/types';

import {FieldFilter} from './field-filter';

type Props = {
    fields: Schema.FieldProperties[];
    activeFilters: FilterObject[];
    entityType: EntityListType;
    openFilters: string[];
    allowNegation: boolean;
    onOpenSection: (filterName: string) => any;
    onCloseSection: (filterName: string) => any;
    onSubmitFilter: (filterObj: FilterObject, field: Schema.FieldProperties) => void;
    onRemoveFilter: (filterKey: string) => void;
    shownColumns?: string[];
    updateShownColumns?: (arg1: string[], arg2: boolean) => void;
};

/**
 * This is used to show a group of filters inside a filter drawer bucket.  By breaking
 * this out from the FilterBucket component, we can group important (primary) filters at
 * the top of a bucket, while hiding the secondary filters behind a "show all" link.
 *
 * @param {Object} props                      - React props
 * @param {Object[]} props.fields             - Array of schema fields
 * @param {string[]} props.openFilters        - List of filter names which should be open,
 *                                              can be a super-set of active filters,
 *                                              as a section can be open without being filled in or applied
 * @param {Object[]} props.activeFilters      - List of actively-applied filter objects
 * @param {string} props.entityType           - The type of entity being filtered ('leads', 'contacts', or 'accounts')
 * @param {Function} props.onOpenSection      - Callback used to open a filter section
 * @param {Function} props.onCloseSection     - Callback used to close a filter section and unapply the filter
 * @param {Function} props.onSubmitFilter     - Callback to apply a filter
 * @param {Function} props.onRemoveFilter     - Callback to remove a filter without closing the section
 * @return {Object}                     - React component for a group of filters
 */
export const FilterBucketSection = (props: Props) => {
    const section = props.fields.map((field) => {
        const filter = props.activeFilters.find((entry) => Object.keys(entry).includes(field.name));

        return (
            <FieldFilter
                key={field.name}
                entityType={props.entityType}
                filter={filter}
                field={field}
                isOpen={props.openFilters.includes(field.name)}
                allowNegation={props.allowNegation}
                onCloseSection={props.onCloseSection}
                onOpenSection={props.onOpenSection}
                onSubmitFilter={props.onSubmitFilter}
                onRemoveFilter={props.onRemoveFilter}
                shownColumns={props.shownColumns}
                updateShownColumns={props.updateShownColumns}
            />
        );
    });

    return <div>{section}</div>;
};
